// In-repo replacement for the abandoned `react-currency-input` package,
// removed because it shipped vulnerable dependencies (babel-cli, jsdom) at
// runtime. The masking below reproduces that library's ATM-style behavior:
// typed digits fill the cents from the right (1234 -> 12.34). One deliberate
// difference: a value that is a plain number ("12.9", 1300) is read as that
// number whatever the separators, so pt-BR's "." thousand separator can't turn
// "12.9" into 129.
import React from 'react';
import { omit } from 'lodash';

type Props = {
  onChange?: Function,
  onChangeEvent?: Function,
  value?: number | string,
  defaultValue?: number | string,
  decimalSeparator?: string,
  thousandSeparator?: string,
  precision?: number | string,
  inputType?: string,
  allowNegative?: boolean,
  allowEmpty?: boolean,
  prefix?: string,
  suffix?: string,
  selectAllOnFocus?: boolean,
  autoFocus?: boolean,
};

type State = {
  maskedValue: string,
};

const OWN_PROPS = [
  'onChange',
  'onChangeEvent',
  'value',
  'defaultValue',
  'decimalSeparator',
  'thousandSeparator',
  'precision',
  'inputType',
  'allowNegative',
  'allowEmpty',
  'prefix',
  'suffix',
  'selectAllOnFocus',
  'autoFocus',
];

export function mask(
  value: any,
  precision: number | string = 2,
  decimalSeparator: string = '.',
  thousandSeparator: string = ',',
  allowNegative: boolean = false,
  prefix: string = '',
  suffix: string = '',
) {
  if (precision < 0) precision = 0;
  if (precision > 20) precision = 20;

  if (value === null || value === undefined) {
    return { value: 0, maskedValue: '' };
  }

  value = String(value);
  if (value.length === 0) {
    return { value: 0, maskedValue: '' };
  }

  // extract digits. if no digits, fill in a zero.
  let digits = value.match(/\d/g) || ['0'];

  let numberIsNegative = false;
  if (allowNegative) {
    const negativeSignCount = (value.match(/-/g) || []).length;
    // number is negative if we have an odd number of "-"
    numberIsNegative = negativeSignCount % 2 === 1;

    let allDigitsAreZero = true;
    for (let idx = 0; idx < digits.length; idx += 1) {
      if (digits[idx] !== '0') {
        allDigitsAreZero = false;
        break;
      }
    }
    if (allDigitsAreZero) {
      numberIsNegative = false;
    }
  }

  // zero-pad the input so there is always an integer digit before the cents
  while (digits.length <= precision) digits.unshift('0');

  if (precision > 0) {
    digits.splice(digits.length - precision, 0, '.');
  }

  // clean up extraneous digits like leading zeros
  digits = Number(digits.join('')).toFixed(precision).split('');
  let raw = Number(digits.join(''));

  let decimalpos = digits.length - precision - 1; // -1 to position before the cents digits
  if (precision > 0) {
    digits[decimalpos] = decimalSeparator;
  } else {
    decimalpos = digits.length;
  }

  for (let x = decimalpos - 3; x > 0; x -= 3) {
    digits.splice(x, 0, thousandSeparator);
  }

  if (prefix.length > 0) digits.unshift(prefix);
  if (suffix.length > 0) digits.push(suffix);

  if (allowNegative && numberIsNegative) {
    digits.unshift('-');
    raw = -raw;
  }

  return {
    value: raw,
    maskedValue: digits.join('').trim(),
  };
}

export default class CurrencyInput extends React.Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = this.prepareState(props);
    this.inputSelectionStart = 1;
    this.inputSelectionEnd = 1;
  }

  componentDidMount() {
    if (!this.theInput) return;
    let selectionStart;
    let selectionEnd;

    if (this.props.autoFocus) {
      this.theInput.focus();
      selectionEnd = this.state.maskedValue.length - this.props.suffix.length;
      selectionStart = selectionEnd;
    } else {
      selectionEnd = Math.min(this.theInput.selectionEnd, this.theInput.value.length - this.props.suffix.length);
      selectionStart = Math.min(this.theInput.selectionStart, selectionEnd);
    }

    // Only touch the selection while focused: on WebKit, setSelectionRange on
    // a blurred input steals focus (upstream react-currency-input bug; its
    // unreleased fix is this same guard).
    if (document.activeElement === this.theInput) {
      this.theInput.setSelectionRange(selectionStart, selectionEnd);
    }
  }

  // eslint-disable-next-line camelcase
  UNSAFE_componentWillReceiveProps(nextProps: Props) {
    this.setState(this.prepareState(nextProps));
  }

  // Must stay a legacy lifecycle: defining any new-API method (like
  // getSnapshotBeforeUpdate) makes React skip UNSAFE_componentWillReceiveProps
  // entirely, and then programmatic value-prop changes stop updating the field.
  // eslint-disable-next-line camelcase
  UNSAFE_componentWillUpdate() {
    if (this.theInput) {
      this.inputSelectionStart = this.theInput.selectionStart;
      this.inputSelectionEnd = this.theInput.selectionEnd;
    }
  }

  componentDidUpdate(prevProps: Props, prevState: State) {
    if (!this.theInput) return;
    const { decimalSeparator, thousandSeparator, prefix, suffix } = this.props;
    const isNegative = (this.theInput.value.match(/-/g) || []).length % 2 === 1;
    const minPos = prefix.length + (isNegative ? 1 : 0);
    let selectionEnd = Math.max(minPos, Math.min(this.inputSelectionEnd, this.theInput.value.length - suffix.length));
    // inputSelectionEnd (not Start) on purpose: verbatim port of the original
    // library's behavior — the selection collapses to a caret after re-render.
    let selectionStart = Math.max(minPos, Math.min(this.inputSelectionEnd, selectionEnd));

    const regexEscapeRegex = /[-[\]{}()*+?.,\\^$|#\s]/g;
    const separatorsRegex = new RegExp(`${decimalSeparator.replace(regexEscapeRegex, '\\$&')}|${thousandSeparator.replace(regexEscapeRegex, '\\$&')}`, 'g');
    const currSeparatorCount = (this.state.maskedValue.match(separatorsRegex) || []).length;
    const prevSeparatorCount = (prevState.maskedValue.match(separatorsRegex) || []).length;
    const adjustment = Math.max(currSeparatorCount - prevSeparatorCount, 0);

    selectionEnd += adjustment;
    selectionStart += adjustment;

    const precision = Number(this.props.precision);

    const baselength = suffix.length
      + prefix.length
      + (precision > 0 ? decimalSeparator.length : 0)
      + precision
      + 1; // account for the default '0' before the decimal separator

    if (this.state.maskedValue.length === baselength) {
      // if we are already at base length, position the cursor at the end
      selectionEnd = this.theInput.value.length - suffix.length;
      selectionStart = selectionEnd;
    }

    // Same focus guard as componentDidMount; the bookkeeping still runs so
    // the next update starts from the computed caret position.
    if (document.activeElement === this.theInput) {
      this.theInput.setSelectionRange(selectionStart, selectionEnd);
    }
    this.inputSelectionStart = selectionStart;
    this.inputSelectionEnd = selectionEnd;
  }

  render() {
    return (
      <input
        ref={input => { this.theInput = input; }}
        type={this.props.inputType}
        value={this.state.maskedValue}
        onChange={this.handleChange}
        onFocus={this.handleFocus}
        onMouseUp={this.handleFocus}
        {...omit(this.props, OWN_PROPS)}
      />
    );
  }

  theInput: ?Object;
  inputSelectionStart: number;
  inputSelectionEnd: number;

  prepareState = (props: Props): State => ({ maskedValue: this.maskFromProps(props).maskedValue });

  maskFromProps = (props: Props) => {
    let initialValue = props.value;
    if (initialValue === null) {
      initialValue = props.allowEmpty ? null : '';
    } else {
      if (typeof initialValue === 'string' && initialValue.trim() !== '' && Number.isFinite(Number(initialValue))) {
        // A number as the API and the forms keep it ("12.9", "1300.0"): its dot is the
        // decimal point, never a thousand separator. Masked text has a prefix or a comma.
        initialValue = Number(initialValue);
      } else if (typeof initialValue === 'string') {
        // Masked text ("R$ 1.300,00"): strip out thousand separators, prefix, suffix, etc.
        if (props.thousandSeparator === '.') {
          initialValue = initialValue.replace(/\./g, '');
        }
        if (props.decimalSeparator !== '.') {
          initialValue = initialValue.replace(new RegExp(props.decimalSeparator, 'g'), '.');
        }
        initialValue = initialValue.replace(/[^0-9-.]/g, '');
        initialValue = Number.parseFloat(initialValue);
      }
      initialValue = Number(initialValue).toLocaleString(undefined, {
        style: 'decimal',
        minimumFractionDigits: props.precision,
        maximumFractionDigits: props.precision,
      });
    }

    return mask(
      initialValue,
      props.precision,
      props.decimalSeparator,
      props.thousandSeparator,
      props.allowNegative,
      props.prefix,
      props.suffix,
    );
  };

  handleChange = (event: Object) => {
    event.preventDefault();
    const { maskedValue, value } = mask(
      event.target.value,
      this.props.precision,
      this.props.decimalSeparator,
      this.props.thousandSeparator,
      this.props.allowNegative,
      this.props.prefix,
      this.props.suffix,
    );

    event.persist(); // allows accessing the event inside the setState callback

    this.setState({ maskedValue }, () => {
      this.props.onChange(maskedValue, value, event);
      this.props.onChangeEvent(event, maskedValue, value);
    });
  };

  handleFocus = (event: Object) => {
    if (!this.theInput) return;

    // whenever we receive focus, check if the position is before the suffix and if not, move it
    const selectionEnd = this.theInput.value.length - this.props.suffix.length;
    const isNegative = (this.theInput.value.match(/-/g) || []).length % 2 === 1;
    const selectionStart = this.props.prefix.length + (isNegative ? 1 : 0);
    if (this.props.selectAllOnFocus) {
      event.target.setSelectionRange(selectionStart, selectionEnd);
    }
    this.inputSelectionStart = selectionStart;
    this.inputSelectionEnd = selectionEnd;
  };
}

CurrencyInput.defaultProps = {
  onChange: () => {},
  onChangeEvent: () => {},
  autoFocus: false,
  value: '0',
  decimalSeparator: '.',
  thousandSeparator: ',',
  precision: '2',
  inputType: 'text',
  allowNegative: false,
  prefix: '',
  suffix: '',
  selectAllOnFocus: false,
};
